import { requireById } from './dom'

/**
 * The contact form. Everything a "book a demo" or "register interest" button
 * on the site points at ends up here, and one POST later it is a message in
 * Slack (see infra/enquiry-form.yml).
 *
 * The endpoint is baked in at build time from the form stack's output. It is
 * empty during local development, which the submit path handles rather than
 * pretending to send.
 */
const ENDPOINT = import.meta.env.VITE_FORM_ENDPOINT
const IS_LOCAL = import.meta.env.VITE_ENV_NAME === 'local'

/** Deliberately loose: the only address we can truly validate is one we mail. */
const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const INTENTS = ['demo', 'interest']

const SEND_FAILED =
  "That didn't get through. Try again, or email hello@toura.au and we'll pick it up there."

export function initEnquiryForm(): void {
  const form = requireById<HTMLFormElement>('eform')
  const done = requireById('edone')
  const error = requireById<HTMLParagraphElement>('eerror')
  const submit = requireById<HTMLButtonElement>('efsubmit')
  const label = submit.textContent ?? 'Send'

  preselectIntent(form)

  form.addEventListener('submit', (event) => {
    event.preventDefault()
    error.hidden = true

    const problem = firstProblem()
    if (problem) {
      error.textContent = problem.message
      error.hidden = false
      problem.field.focus()
      return
    }

    submit.disabled = true
    submit.textContent = 'Sending…'

    void send(read(form))
      .then(() => {
        form.hidden = true
        done.hidden = false
        // Moving focus is what tells a screen-reader user the form is gone and
        // the acknowledgement has taken its place.
        done.focus()
        if (!ENDPOINT && IS_LOCAL) {
          const note = requireById('edev')
          note.textContent =
            'Local build: nothing was sent. Set VITE_FORM_ENDPOINT in .env.local.local to post at the deployed Lambda.'
          note.hidden = false
        }
      })
      .catch((reason: unknown) => {
        console.error('[enquiry] submission failed', reason)
        error.textContent = reason instanceof Error ? reason.message : SEND_FAILED
        error.hidden = false
        submit.disabled = false
        submit.textContent = label
      })
  })
}

/**
 * "Register interest" links carry ?intent=interest, so the person arrives with
 * the choice they already made on the previous page selected.
 */
function preselectIntent(form: HTMLFormElement): void {
  const wanted = new URLSearchParams(location.search).get('intent')
  if (!wanted || !INTENTS.includes(wanted)) return

  const option = form.querySelector<HTMLInputElement>(
    `input[name="intent"][value="${wanted}"]`,
  )
  if (option) option.checked = true
}

interface Enquiry {
  intent: string
  name: string
  email: string
  phone: string
  business: string
  message: string
  website: string
  source: string
}

function read(form: HTMLFormElement): Enquiry {
  const data = new FormData(form)
  const field = (name: string) => String(data.get(name) ?? '').trim()

  return {
    intent: field('intent') || 'demo',
    name: field('name'),
    email: field('email'),
    phone: field('phone'),
    business: field('business'),
    message: field('message'),
    website: field('website'),
    source: source(),
  }
}

/** Which page sent them, so the Slack message says where the interest started. */
function source(): string {
  if (!document.referrer) return 'direct'
  try {
    const url = new URL(document.referrer)
    return url.host === location.host ? url.pathname : document.referrer
  } catch {
    return 'direct'
  }
}

interface Problem {
  field: HTMLElement
  message: string
}

/**
 * Only the two fields we cannot contact anyone without are checked. The rest
 * of the form is optional on purpose, so there is nothing else to fail on.
 */
function firstProblem(): Problem | null {
  const name = requireById<HTMLInputElement>('ef-name')
  const email = requireById<HTMLInputElement>('ef-email')

  if (!name.value.trim()) {
    return { field: name, message: 'We need a name to call you by.' }
  }
  if (!email.value.trim()) {
    return { field: email, message: 'We need an email address to reply to.' }
  }
  if (!LOOKS_LIKE_EMAIL.test(email.value.trim())) {
    return { field: email, message: "That email address doesn't look right." }
  }
  return null
}

async function send(enquiry: Enquiry): Promise<void> {
  if (!ENDPOINT) {
    // No back end in this build. Locally that is the everyday state and worth
    // saying out loud; in a deployed build it is a broken deploy, and quietly
    // thanking someone whose message went nowhere would be the worst outcome.
    if (!IS_LOCAL) throw new Error(SEND_FAILED)
    console.warn('[enquiry] no VITE_FORM_ENDPOINT, so nothing was sent:', enquiry)
    return
  }

  const response = await fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(enquiry),
  })

  if (!response.ok) {
    // The Lambda explains what it rejected (a malformed address, say); its
    // wording is better than a generic failure, so prefer it when there is one.
    const body: unknown = await response.json().catch(() => null)
    const reason =
      body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string'
        ? (body as { error: string }).error
        : SEND_FAILED
    throw new Error(reason)
  }
}
